import { Edit, List, ShowButton, useTable } from '@refinedev/antd';
import {
  IExperienceEvent,
  IExperienceReservation,
  ReservationStatus,
} from '../../../interfaces/experience';
import { Button, Checkbox, Dropdown, Image, Input, Space, Table, Tag } from 'antd';
import { HttpError, LogicalFilter, useInvalidate, useNavigation } from '@refinedev/core';
import { MoreOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ReferenceSelect } from '../../../components/form-item/ReferenceSelect';
import { DateFilter } from '../../../components/form-item/DateFilter';
import { EntryDialog } from './EntryDialog';
import { useState } from 'react';
import { EditDialog } from './EditDialog';
import { FinishButton } from './FinishButton';
import { CancelDialog } from './CancelDialog';

const STATUS: Record<ReservationStatus, { label: string; color: string }> = {
  available: {
    label: '受付待ち',
    color: 'blue',
  },
  started: {
    label: '体験中',
    color: 'green',
  },
  finished: {
    label: '終了',
    color: 'gray',
  },
  canceled: {
    label: 'キャンセル',
    color: 'red',
  },
};

export const ExperienceReservationList = () => {
  const { showUrl } = useNavigation();
  const [startId, setStartId] = useState<string>();
  const [editId, setEditId] = useState<string>();
  const [cancelId, setCancelId] = useState<string>();
  const invalidate = useInvalidate();

  const { tableProps, filters, setFilters } = useTable<IExperienceReservation>({
    syncWithLocation: true,
    filters: {
      initial: [
        {
          field: 'date',
          operator: 'eq',
          value: new Date()
            .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
            .split('/')
            .join('-'),
        },
      ],
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    pagination: {
      pageSize: 100,
    },
    meta: {
      fields: [
        'id',
        'status',
        'email',
        'date',
        'startTime',
        'startTimeInt',
        'endTime',
        'endTimeInt',
        'timeLength',
        {
          event: ['id', 'name'],
          user: [
            'id',
            'email',
            {
              profile: ['id', 'name'],
            },
          ],
          sessions: [
            'id',
            {
              child: [
                'id',
                'name',
                'gender',
                'ageYear',
                {
                  avatar: ['id', 'url'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  const Header = () => (
    <Space wrap>
      <DateFilter currentFilters={filters as LogicalFilter[]} setFilters={setFilters} />
      <ReferenceSelect
        resource="experienceEvents"
        optionLabel="name"
        label="イベント"
        allowClear
        style={{ width: 200 }}
        value={
          (filters as LogicalFilter[]).find((filter) => filter.field === 'event_id')
            ?.value as string
        }
        onChange={(value) => {
          setFilters([{ field: 'event_id', operator: 'eq', value }]);
        }}
      />
      <Checkbox.Group
        value={
          (filters as LogicalFilter[]).find((filter) => filter.field === 'status')
            ?.value as string[]
        }
        onChange={(value) => {
          if (value.length === 0) {
            setFilters(
              (filters as LogicalFilter[]).filter((filter) => filter.field !== 'status'),
              'replace',
            );
          } else {
            setFilters([{ field: 'status', operator: 'in', value }]);
          }
        }}
        options={Object.keys(STATUS).map((status) => ({
          label: STATUS[status as ReservationStatus].label,
          value: status,
        }))}
      />
      <Input.Search
        placeholder="メールアドレス"
        defaultValue={
          (filters as LogicalFilter[]).find((filter) => filter.field === 'email')?.value as string
        }
        onSearch={(value) => {
          setFilters([{ field: 'email', operator: 'contains', value }]);
        }}
      />
    </Space>
  );

  return (
    <List headerProps={{ title: '予約一覧' }} headerButtons={<Header />}>
      <Table
        {...tableProps}
        rowKey="id"
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total) => `全${total}件`,
        }}
      >
        <Table.Column dataIndex="date" title="日付" sorter={{ multiple: 1 }} />
        <Table.Column<IExperienceReservation>
          dataIndex="startTimeInt"
          title="開始"
          sorter={{ multiple: 2 }}
          render={(value, record) => record.startTime}
        />
        <Table.Column<IExperienceReservation>
          dataIndex="endTimeInt"
          title="終了"
          sorter={{ multiple: 3 }}
          render={(value, record) => record.endTime}
        />
        <Table.Column<IExperienceReservation>
          dataIndex="status"
          title="ステータス"
          sorter={{ multiple: 4 }}
          render={(value: ReservationStatus) => (
            <Tag color={STATUS[value].color}>{STATUS[value].label}</Tag>
          )}
        />
        <Table.Column
          dataIndex="user"
          title="予約者"
          render={(value) => (
            <Link to={showUrl('users', value.id)}>
              {value.profile.name}
              <br />
              {value.email}
            </Link>
          )}
        />
        <Table.Column
          dataIndex="sessions"
          title="子供"
          render={(value) => (
            <Space direction="vertical">
              {value.map((session: any) => (
                <Space>
                  {session.child.avatar.url && (
                    <Image src={session.child.avatar.url} width={60} height={60} preview={false} />
                  )}
                  <Link to={showUrl('children', session.child.id)}>
                    {session.child.name}
                    {session.child.gender === 'boy' ? 'くん' : 'ちゃん'}({session.child.ageYear}歳)
                  </Link>
                </Space>
              ))}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="event"
          title="イベント"
          render={(value) => <Link to={showUrl('experienceEvents', value.id)}>{value.name}</Link>}
        />
        <Table.Column<IExperienceReservation>
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              {record.status === 'available' ? (
                <Button type="primary" onClick={() => setStartId(record.id)}>
                  受付
                </Button>
              ) : record.status === 'started' ? (
                <FinishButton recordId={record.id} />
              ) : (
                <></>
              )}
              <Dropdown
                menu={{
                  items: [
                    { key: '1', label: '変更', onClick: () => setEditId(record.id) },
                    { key: '2', label: 'キャンセル', onClick: () => setCancelId(record.id) },
                  ],
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} type="text" />
              </Dropdown>
            </Space>
          )}
        />
      </Table>
      <EntryDialog
        startId={startId}
        onStart={async () => {
          await invalidate({
            resource: 'experienceReservations',
            invalidates: ['list'],
          });
          setStartId(undefined);
        }}
        onClose={() => setStartId(undefined)}
      />
      <EditDialog onClose={() => setEditId(undefined)} editId={editId} />
      <CancelDialog onClose={() => setCancelId(undefined)} cancelId={cancelId} />
    </List>
  );
};
